// Auto-recover from stale chunk loading errors after a deploy.
// When Vite ships new hashed chunk filenames, an old cached index.html may
// reference URLs that no longer exist. We detect that, clear SW + caches,
// and reload once.

const RECOVERY_KEY = "thnx_chunk_recover";
const CHUNK_ERROR_RE =
  /Importing a module script failed|Failed to fetch dynamically imported module|ChunkLoadError|error loading dynamically imported module/i;

export function isChunkLoadError(message: string | undefined | null): boolean {
  if (!message) return false;
  return CHUNK_ERROR_RE.test(message);
}

export async function hardResetApp(): Promise<void> {
  try {
    if ("serviceWorker" in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
    }
    if ("caches" in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    }
  } catch {
    // best-effort
  }
  window.location.reload();
}

export function initChunkErrorRecovery(): void {
  if (typeof window === "undefined") return;

  const tryRecover = (msg: string | undefined) => {
    if (!isChunkLoadError(msg)) return;
    try {
      if (sessionStorage.getItem(RECOVERY_KEY)) return; // already tried
      sessionStorage.setItem(RECOVERY_KEY, "1");
    } catch {
      // sessionStorage blocked — still attempt one recovery
    }
    void hardResetApp();
  };

  window.addEventListener("error", (e) => {
    tryRecover(e?.message || (e?.error && (e.error as Error).message));
  });

  window.addEventListener("unhandledrejection", (e) => {
    const reason = (e as PromiseRejectionEvent).reason;
    const msg =
      typeof reason === "string"
        ? reason
        : reason && typeof reason === "object" && "message" in reason
        ? String((reason as { message: unknown }).message)
        : undefined;
    tryRecover(msg);
  });

  // Clear the guard once the app successfully boots past first paint.
  window.addEventListener("load", () => {
    setTimeout(() => {
      try {
        sessionStorage.removeItem(RECOVERY_KEY);
      } catch {
        // ignore
      }
    }, 5000);
  });
}
